import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Button,
  Typography,
  Box,
  CircularProgress,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import { DataGrid } from "@mui/x-data-grid";

function fmtVal(v) {
  if (v === null || v === undefined) return "—";
  const n = parseFloat(v);
  return isNaN(n) ? String(v) : n.toFixed(2);
}

function fmtDt(dt) {
  if (!dt) return "—";
  try {
    return new Date(dt).toLocaleString("en-GB", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return String(dt);
  }
}

const COLUMNS = [
  {
    field: "create_date",
    headerName: "Date & Time",
    flex: 3,
    valueFormatter: (value) => fmtDt(value),
  },
  {
    field: "gender",
    headerName: "Gender",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => value || "—",
  },
  {
    field: "lab_min_val",
    headerName: "Lab Min",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => fmtVal(value),
  },
  {
    field: "lab_max_val",
    headerName: "Lab Max",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => fmtVal(value),
  },
  {
    field: "min_val",
    headerName: "Clin Min",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => fmtVal(value),
  },
  {
    field: "max_val",
    headerName: "Clin Max",
    flex: 2,
    align: "center",
    headerAlign: "center",
    valueFormatter: (value) => fmtVal(value),
  },
];

function HistorySection({ label, rows, color }) {
  const tagged = rows.map((r, i) => ({ ...r, id: i }));
  return (
    <Box sx={{ mb: 3 }}>
      <Typography
        variant="subtitle2"
        sx={{
          px: 1.5,
          py: 0.5,
          mb: 0.5,
          backgroundColor: color,
          borderRadius: 1,
          fontWeight: 700,
          fontSize: "0.8rem",
        }}
      >
        {label}
      </Typography>
      {tagged.length === 0 ? (
        <Box
          sx={{
            px: 2,
            py: 1,
            backgroundColor: "#fff8e1",
            color: "#856404",
            borderRadius: 1,
            fontSize: "0.8rem",
          }}
        >
          No historical changes recorded.
        </Box>
      ) : (
        <DataGrid
          density="compact"
          autoHeight
          rows={tagged}
          columns={COLUMNS}
          hideFooter={tagged.length <= 100}
          pageSize={100}
          disableRowSelectionOnClick
          sx={{ fontSize: "0.78rem" }}
        />
      )}
    </Box>
  );
}

export default function RangeHistoryDialog({
  open,
  onClose,
  rangeHistory,
  loading,
}) {
  const catName = rangeHistory?.cat_name ?? "";
  const subName = rangeHistory?.sub_name ?? "";
  const none = rangeHistory?.history_none ?? [];
  const male = rangeHistory?.history_male ?? [];
  const female = rangeHistory?.history_female ?? [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        Range History — {catName} / {subName}
        <IconButton
          aria-label="close"
          onClick={onClose}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", mb: 2 }}
        >
          Only records where Lab Min, Lab Max, Clin Min or Clin Max changed are
          shown. Ordered latest first.
        </Typography>

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <HistorySection
              label="── No Gender Set ──"
              rows={none}
              color="#d0e8ff"
            />
            <HistorySection label="── Male ──" rows={male} color="#d0f0d0" />
            <HistorySection
              label="── Female ──"
              rows={female}
              color="#ffd0e8"
            />

            <Typography variant="caption" color="text.secondary">
              Records shown — No Gender: {none.length} | Male: {male.length} |
              Female: {female.length}
            </Typography>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
}
